import { useParams, useLocation } from 'react-router-dom';
import { getCategoryResults, getCategoryResultsWithoutAnalytics, addCategoryResult, getHourse, getRiders,updateCategoryResult,deleteResult, uploadSheet, uploadSheetWithoutAnalytics } from '../../../Utils/apiServices';
import { Table, Button, Modal, Form, Select, message, Input, Upload } from 'antd';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';

const EventResults = () => {
    const { id } = useParams();
    const location = useLocation();
    const locationWithAnalytics = location.state?.withAnalytics;
    const [results, setResults] = useState([]);
    const [horses, setHorses] = useState([]);
    const [riders, setRiders] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [editingRecord, setEditingRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [withAnalytics, setWithAnalytics] = useState(() => {
        if (typeof locationWithAnalytics !== 'undefined') {
            return !!locationWithAnalytics;
        }
        return true;
    });

    const fetchResults = useCallback(async () => {
        try {
            const response = withAnalytics
                ? await getCategoryResults(id)
                : await getCategoryResultsWithoutAnalytics(id);

            const data = response?.data?.data || [];
            setResults(data);

            if (
                typeof locationWithAnalytics === 'undefined' &&
                data.length &&
                typeof data[0].with_analystics !== 'undefined'
            ) {
                const backendAnalyticsFlag = !!data[0].with_analystics;
                if (backendAnalyticsFlag !== withAnalytics) {
                    setWithAnalytics(backendAnalyticsFlag);
                }
            }
        } catch (error) {
            console.error('Error fetching results:', error);
            message.error('Failed to fetch results');
        }
    }, [id, withAnalytics, locationWithAnalytics]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    useEffect(() => {
        if (typeof locationWithAnalytics !== 'undefined') {
            setWithAnalytics(!!locationWithAnalytics);
        }
    }, [locationWithAnalytics]);

    useEffect(() => {
        if (withAnalytics) {
            fetchHorsesAndRiders();
        } else {
            setHorses([]);
            setRiders([]);
        }
    }, [withAnalytics]);

    useEffect(() => {
        form.resetFields();
        setEditingRecord(null);
        setIsModalOpen(false);
    }, [withAnalytics, form]);

    const filteredResults = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return results;
        }

        const includesTerm = (value) => {
            if (value === null || value === undefined) {
                return false;
            }
            return String(value).toLowerCase().includes(term);
        };

        return results.filter((record) => {
            if (withAnalytics) {
                return (
                    includesTerm(record.horseName) ||
                    includesTerm(record.riderName) ||
                    includesTerm(record.order) ||
                    includesTerm(record.rank) ||
                    includesTerm(record.penality) ||
                    includesTerm(record.penalty_2) ||
                    includesTerm(record.duration) ||
                    includesTerm(record.timestamps) ||
                    includesTerm(record.percentage)
                );
            }

            return (
                includesTerm(record.rider_name || record.riderName) ||
                includesTerm(record.horse_name || record.horseName) ||
                includesTerm(record.rank) ||
                includesTerm(record.penality) ||
                includesTerm(record.penalty_2) ||
                includesTerm(record.timestamps)
            );
        });
    }, [results, searchTerm, withAnalytics]);

    const [modal, contextHolder] = Modal.useModal();

    const getResultRecordId = (record) =>
        record?.id ?? record?.event_result_id ?? record?.result_id;

    const performDeleteResult = async (resultId) => {
        try {
            await deleteResult({ id: resultId });
            message.success('Result deleted successfully');
            await fetchResults();
        } catch (error) {
            console.error('Error deleting result:', error);
            message.error('Failed to delete result');
            throw error;
        }
    };

    const requestDeleteResult = (record) => {
        const resultId = getResultRecordId(record);
        if (resultId == null || resultId === '') {
            message.error('Cannot delete: missing result id');
            return;
        }
        modal.confirm({
            title: 'Delete result',
            content: 'Are you sure you want to delete this result? This action cannot be undone.',
            okText: 'Delete',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => performDeleteResult(resultId),
        });
    };

    const fetchHorsesAndRiders = async () => {
        try {
            const horsesResponse = await getHourse();
            const ridersResponse = await getRiders();
            setHorses(horsesResponse.data.data);
            setRiders(ridersResponse.data.data);    
        } catch (error) {
            message.error('Failed to fetch horses and riders');
        }
    };

    const analyticsColumns = [
        {
            title: 'Order',
            dataIndex: 'order',
            key: 'order'
        },
        {
            title: 'Horse Name',
            dataIndex: 'horseName',
            key: 'horseName'
        },
        {
            title: 'Rider Name',
            dataIndex: 'riderName',
            key: 'riderName'
        },
        {
            title: 'Cleared',
            dataIndex: 'cleared',
            key: 'cleared',
            render: (cleared) => cleared ? 'Yes' : 'No'
        },
        {
            title: 'Rank',
            dataIndex: 'rank',
            key: 'rank'
        },
        {
            title: 'Penalty',
            dataIndex: 'penality',
            key: 'penality'
        },
        {
            title: 'Penalty 2',
            dataIndex: 'penalty_2',
            key: 'penalty_2'
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration'
        },
        {
            title: 'Timestamps',
            dataIndex: 'timestamps',
            key: 'timestamps'
        },
        {
            title: 'Percentage',
            dataIndex: 'percentage',
            key: 'percentage'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <>
                    <Button type="link" onClick={() => handleEdit(record)}>
                        Edit
                    </Button>
                    <Button 
                        type="link" 
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            requestDeleteResult(record);
                        }}
                    />
                </>
            )
        }
    ];

    const basicColumns = [
        {
            title: 'Rider Name',
            dataIndex: 'rider_name',
            key: 'rider_name',
            render: (value, record) => value || record.riderName || '-'
        },
        {
            title: 'Horse Name',
            dataIndex: 'horse_name',
            key: 'horse_name',
            render: (value, record) => value || record.horseName || '-'
        },
        {
            title: 'Rank',
            dataIndex: 'rank',
            key: 'rank',
            render: (value) => (value || value === 0 ? value : '-')
        },
        {
            title: 'Penalties 1',
            dataIndex: 'penality',
            key: 'penality',
            render: (value, record) => {
                const fallback = record.penalty;
                return value === 0 || value ? value : (fallback === 0 || fallback ? fallback : '-');
            }
        },
        {
            title: 'Penalties 2',
            dataIndex: 'penalty_2',
            key: 'penalty_2',
            render: (value) => (value === 0 || value ? value : '-')
        },
        {
            title: 'Time',
            dataIndex: 'timestamps',
            key: 'timestamps',
            render: (value) => value || '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <>
                    <Button type="link" onClick={() => handleEdit(record)}>
                        Edit
                    </Button>
                    <Button 
                        type="link" 
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            requestDeleteResult(record);
                        }}
                    />
                </>
            )
        }
    ];

    const columns = withAnalytics ? analyticsColumns : basicColumns;

    const handleEdit = (record) => {
        setEditingRecord(record);
        if (withAnalytics) {
            form.setFieldsValue({
                horse_id: record.horse_id,
                rider_id: record.rider_id,
                cleared: record.cleared,
                rank: record.rank,
                penalty: record.penality,
                penalty_2: record.penalty_2,
                duration: record.duration,
                timestamps: record.timestamps,
                percentage: record.percentage,
                order: record.order
            });
        } else {
            form.setFieldsValue({
                rider_name: record.rider_name || record.riderName || '',
                horse_name: record.horse_name || record.horseName || '',
                rank: record.rank,
                penality: record.penality,
                penalty_2: record.penalty_2,
                timestamps: record.timestamps
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (values) => {
        try {
            let payload;

            if (withAnalytics) {
                payload = {
                    horse_id: Number(values.horse_id),
                    rider_id: values.rider_id ? Number(values.rider_id) : null,
                    cleared: values.cleared,
                    rank: values.rank ? values.rank : null,
                    penality: values.penalty ? Number(values.penalty) : null,
                    penalty_2: values.penalty_2 ? Number(values.penalty_2) : null,
                    duration: values.duration || null,
                    timestamps: values.timestamps || null,
                    percentage: values.percentage ? Number(parseFloat(values.percentage).toFixed(3)) : null,
                    order: values.order ? Number(values.order) : null,
                    category_id: Number(id)
                };
            } else {
                payload = {
                    rider_name: values.rider_name || null,
                    horse_name: values.horse_name || null,
                    rank: values.rank ? Number(values.rank) : null,
                    penality: values.penality === undefined || values.penality === '' ? null : Number(values.penality),
                    penalty_2: values.penalty_2 === undefined || values.penalty_2 === '' ? null : Number(values.penalty_2),
                    timestamps: values.timestamps && values.timestamps !== '' ? values.timestamps : null,
                    category_id: Number(id)
                };
            }
            
            if (editingRecord) {
                payload.id = editingRecord.id;
                await updateCategoryResult(payload);
                message.success('Result updated successfully');
            } else {
                await addCategoryResult(payload);
                message.success('Result added successfully');
            }
            
            setIsModalOpen(false);
            setEditingRecord(null);
            form.resetFields();
            await fetchResults();
        } catch (error) {
            console.error('Error saving result:', error);
            message.error(`Failed to ${editingRecord ? 'update' : 'add'} result`);
        }
    };

    const handleAddClick = () => {
        setEditingRecord(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleUploadSheet = async (info) => {
        console.log(info,'12');
        const file = info.file?.originFileObj || info.file;
        
        if (!file) {
            message.error('No file selected');
            return;
        }
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('categoryId', id);
            
            console.log('FormData:', formData);
            console.log('File:', file);
            console.log('CategoryId:', id);
            
            if (withAnalytics) {
                await uploadSheet(formData);
            } else {
                await uploadSheetWithoutAnalytics(formData);
            }
            message.success('Sheet uploaded successfully');
            await fetchResults(); // Refresh the results after upload
        } catch (error) {
            console.error('Error uploading sheet:', error);
            message.error('Failed to upload sheet');
        }
    };

    // const uploadProps = {
    //     beforeUpload: (file) => {
    //         const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    //         if (!isCSV) {
    //             message.error('You can only upload CSV files!');
    //             return false;
    //         }
    //         return false; // Prevent auto upload
    //     },
    //     onChange: (info) => {
    //         console.log(info,'12');
    //         if (info.file && info.file.originFileObj) {
    //             handleUploadSheet(info.file.originFileObj);
    //         }
    //     },
    //     showUploadList: false,
    //     accept: '.csv'
    // };

    return (
        <div className="content-wrapper" style={{ padding: '24px' }}>
            {contextHolder}
            <div className="header-row" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '24px',
                marginTop: '30px',
                flexWrap: 'wrap',
                gap: '16px'
            }}>
                <h1 style={{ 
                    margin: 0, 
                    fontSize: '24px', 
                    fontWeight: '500',
                    flexShrink: 0
                }}>Results</h1>
                <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end'
                }}>
                    <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                        placeholder="Search by rider, horse, rank..."
                        style={{ minWidth: '220px' }}
                    />
                    <Upload 
                        onChange={handleUploadSheet}
                        beforeUpload={() => false}
                        showUploadList={false}
                        accept=".csv"
                    >
                        <Button 
                            icon={<UploadOutlined />}
                            size="large"
                            style={{ 
                                borderRadius: '6px'
                            }}
                        >
                            Upload Sheet
                        </Button>
                    </Upload>
                    <Button 
                        type="primary" 
                        onClick={handleAddClick}
                        size="large"
                        style={{ 
                            borderRadius: '6px'
                        }}
                    >
                        Add Result
                    </Button>
                </div>
            </div>
            
            <Table 
                columns={columns}
                dataSource={filteredResults}
                rowKey={(row) => getResultRecordId(row) ?? `row-${row.order}-${row.riderName}-${row.horseName}`}
                style={{ 
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                    showTotal: (total) => `Total ${total} items`
                }}
            />

            <Modal
                title={editingRecord ? "Edit Result" : "Add Result"}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    setEditingRecord(null);
                    form.resetFields();
                }}
                footer={null}
                width={500}
                style={{ top: 20 }}
                bodyStyle={{ padding: '24px' }}
            >
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    style={{ maxWidth: '100%' }}
                >
                    {withAnalytics ? (
                        <>
                            <Form.Item
                                name="horse_id"
                                label="Horse"
                                rules={[{ required: true }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {horses.map(horse => (
                                        <Select.Option key={horse.id} value={horse.id}>
                                            {horse.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="rider_id"
                                label="Rider"
                            >
                                <Select 
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {riders.map(rider => (
                                        <Select.Option key={rider.id} value={rider.id}>
                                            {rider.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="cleared"
                                label="Cleared"
                            >
                                <Select>
                                    <Select.Option value={true}>Yes</Select.Option>
                                    <Select.Option value={false}>No</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="rank"
                                label="Rank"
                            >
                                <Input 
                                    type="text"
                                    placeholder="Enter rank"
                                />
                            </Form.Item>

                            <Form.Item
                                name="penalty"
                                label="Penalty"
                            >
                                <Input 
                                    type="number" 
                                    min={0}
                                    step="0.01"
                                    placeholder="Enter penalty points"
                                />
                            </Form.Item>

                            <Form.Item
                                name="penalty_2"
                                label="Penalty 2"
                            >
                                <Input 
                                    type="number" 
                                    min={0}
                                    step="1"
                                    placeholder="Enter second penalty"
                                />
                            </Form.Item>

                            <Form.Item
                                name="duration"
                                label="Duration"
                            >
                                <Input 
                                    type="text"
                                    placeholder="Enter duration"
                                />
                            </Form.Item>

                            <Form.Item
                                name="timestamps"
                                label="Timestamps"
                            >
                                <Input 
                                    type="text"
                                    placeholder="Enter timestamps (e.g., 0:09, 1:54)"
                                />
                            </Form.Item>

                            <Form.Item
                                name="percentage"
                                label="Percentage"
                            >
                                <Input 
                                    type="number" 
                                    min={0}
                                    max={100}
                                    step="0.001"
                                    placeholder="Enter percentage"
                                />
                            </Form.Item>

                            <Form.Item
                                name="order"
                                label="Order"
                            >
                                <Input 
                                    type="number" 
                                    min={1}
                                    step="1"
                                    placeholder="Enter order"
                                />
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <Form.Item
                                name="rider_name"
                                label="Rider Name"
                                rules={[{ required: true, message: 'Please enter rider name' }]}
                            >
                                <Input placeholder="Enter rider name" />
                            </Form.Item>

                            <Form.Item
                                name="horse_name"
                                label="Horse Name"
                                rules={[{ required: true, message: 'Please enter horse name' }]}
                            >
                                <Input placeholder="Enter horse name" />
                            </Form.Item>

                            <Form.Item
                                name="rank"
                                label="Rank"
                            >
                                <Input 
                                    type="number"
                                    min={1}
                                    step="1"
                                    placeholder="Enter rank"
                                />
                            </Form.Item>

                            <Form.Item
                                name="penality"
                                label="Penalties 1"
                            >
                                <Input 
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    placeholder="Enter penalties 1"
                                />
                            </Form.Item>

                            <Form.Item
                                name="penalty_2"
                                label="Penalties 2"
                            >
                                <Input 
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    placeholder="Enter penalties 2"
                                />
                            </Form.Item>

                            <Form.Item
                                name="timestamps"
                                label="Time"
                            >
                                <Input 
                                    placeholder="Enter time"
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Button 
                            type="primary" 
                            htmlType="submit"
                            size="large"
                            style={{ minWidth: '120px', borderRadius: '6px' }}
                        >
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default EventResults;